import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, ActionIcon, Popover } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';
import { getDateInputPattern } from '@/config/dateLocales';

interface DateInputWithMaskProps {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  leftSection?: React.ReactNode;
  className?: string;
}

export const DateInputWithMask = ({
  label,
  placeholder,
  value,
  onChange,
  disabled = false,
  error,
  required = false,
  leftSection,
  className,
}: DateInputWithMaskProps) => {
  const { t, i18n } = useTranslation();
  const { formatForInput, parseDate } = useLocalizedDate();
  const [opened, setOpened] = useState(false);

  // Get locale-specific input pattern
  const inputPattern = getDateInputPattern(i18n.language);
  const localePlaceholder = placeholder || inputPattern.placeholder;

  // Watch language changes and reformat existing value
  useEffect(() => {
    if (value && value.trim() !== '') {
      // Parse current value (using old locale format)
      const dateObj = parseDate(value);
      if (dateObj && !isNaN(dateObj.getTime())) {
        // Reformat to new locale format
        const reformatted = formatForInput(dateObj);
        if (reformatted !== value) {
          onChange(reformatted);
        }
      }
    }
    // Only watch language changes, not value (to prevent infinite loop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Format the input value based on locale
  const formatDateInput = useCallback((input: string): string => {
    // Remove all non-numeric characters
    const numbers = input.replace(/\D/g, '');

    // Format based on locale separator (. for PL, / for EN)
    const separator = inputPattern.separator;
    let formatted = '';

    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += separator;
      }
      formatted += numbers[i];
    }

    return formatted;
  }, [inputPattern.separator]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.currentTarget.value;
    const formattedValue = formatDateInput(inputValue);
    onChange(formattedValue);
  }, [onChange, formatDateInput]);

  const handleCalendarChange = useCallback((dateString: string | null) => {
    if (dateString) {
      const date = new Date(dateString);
      const formattedDate = formatForInput(date);
      onChange(formattedDate);
    }
    setOpened(false);
  }, [onChange, formatForInput]);

  const currentDate = parseDate(value);
  const currentDateString = currentDate ? currentDate.toISOString().split('T')[0] : null;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withArrow
      shadow="md"
      withinPortal
      zIndex={Z_INDEX_MOBILE_MODAL_DATEPICKER}
    >
      <Popover.Target>
        <TextInput
          className={className}
          label={label}
          placeholder={localePlaceholder}
          value={value}
          onChange={handleInputChange}
          onClick={() => !disabled && setOpened(true)}
          disabled={disabled}
          error={error}
          required={required}
          leftSection={leftSection}
          rightSection={
            <ActionIcon
              variant="subtle"
              onClick={() => setOpened(!opened)}
              disabled={disabled}
              aria-label={t('accessibility.openCalendar')}
            >
              <IconCalendar size={16} />
            </ActionIcon>
          }
          maxLength={10}
          autoComplete="off"
        />
      </Popover.Target>

      <Popover.Dropdown>
        <DatePicker
          value={currentDateString}
          onChange={handleCalendarChange}
          minDate={new Date().toISOString().split('T')[0]}
        />
      </Popover.Dropdown>
    </Popover>
  );
};
